import { module } from "angular";
import { map } from "rxjs/operators";

import type { Observable } from "rxjs";

import type { IHttpService } from "App/Modules/Services";

import type {
    IEquipment,
    IEquipmentDetails,
    IEquipmentSlim,
    IOperatorStationSlim,
    IResponse,
    ISiteSlim,
} from "App/Modules/Models";
import { EquipmentFunctions } from "App/Modules/Models";

import type { IEquipmentRepository } from "./IEquipmentRepository";

export default class EquipmentRepository implements IEquipmentRepository {
    public static $inject: string[] = ["mmHttpService"];

    constructor(private readonly httpService: IHttpService) {}

    public getEquipment(search?: string): Observable<IResponse<IEquipmentSlim[]>> {
        return this.httpService.get<IEquipmentSlim[]>(`equipment?$search=${search}`).pipe(
            map((response) => {
                response.data = response.data.map((eq) => EquipmentFunctions.enhanceEquipment(eq));
                return response;
            }),
        );
    }

    public getEquipmentList(): Observable<IResponse<IEquipmentSlim[]>> {
        return this.httpService.get<IEquipmentSlim[]>("equipment").pipe(
            map((response) => {
                response.data = response.data.map((eq) => EquipmentFunctions.enhanceEquipment(eq));
                return response;
            }),
        );
    }

    public getSitesForEquipment(equipmentId: string): Observable<IResponse<ISiteSlim[]>> {
        return this.httpService.get<ISiteSlim[]>(`equipment/${equipmentId}/sites`);
    }

    public getDecoupledOperatorStations(): Observable<IResponse<IOperatorStationSlim[]>> {
        return this.httpService.get<IOperatorStationSlim[]>("equipment/decoupledOperatorStations");
    }

    public searchDecoupledOperatorStations(search: string): Observable<IResponse<IOperatorStationSlim[]>> {
        return this.httpService.get<IOperatorStationSlim[]>(`equipment/decoupledOperatorStations?$search=${search}`);
    }

    public findEquipment(equipmentId: string): Observable<IResponse<IEquipment>> {
        return this.httpService.get<IEquipment>(`equipment/${equipmentId}`).pipe(
            map((response) => {
                response.data = EquipmentFunctions.enhanceEquipment(response.data);
                return response;
            }),
        );
    }

    public getEquipmentsForSite(friendlyId: string): Observable<IResponse<IEquipmentSlim[]>> {
        return this.httpService.get<IEquipmentSlim[]>(`equipment/forSite/${friendlyId}`);
    }

    public addEquipment(equipment: IEquipmentDetails): Observable<IResponse<IEquipmentDetails>> {
        return this.httpService.post<IEquipmentDetails>("equipment", equipment);
    }

    public addOperatorStation(operatorStation: IOperatorStationSlim): Observable<IResponse<IOperatorStationSlim>> {
        return this.httpService.post<IOperatorStationSlim>("equipment/operatorStation", operatorStation);
    }

    public patchOperatorStation(
        operatorStationId: string,
        operatorStationPatch: IOperatorStationSlim,
    ): Observable<IResponse<IOperatorStationSlim>> {
        return this.httpService.patch<IOperatorStationSlim>(
            `equipment/operatorStation/${operatorStationId}`,
            operatorStationPatch,
        );
    }
}

module("mmRepositories").service("mmEquipmentRepository", EquipmentRepository);
